import {
  DEFAULT_PUBLIC_LANGUAGE,
  type PublicLanguage,
  productName,
  say,
} from '@app/shared'
import { PRODUCT_DIRECTORIES, type Product } from '@app/shared/brand'
import { LayoutGrid, Menu as MenuIcon, X } from 'lucide-react'
import {
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { placeLine } from '../lib/address-place'
import {
  currentProduct,
  isPlatformSurface,
  orgHref,
  siteCarriesTimes,
} from '../lib/public-router'
import { Button } from './Button'
import { Menu } from './Menu'
import { PoweredBy } from './PoweredBy'
import { ThemeToggle } from './theme'
import { useDismissable } from './useDismissable'
import { useFollowInPlace } from './useFollowInPlace'
import { WayIn } from './WayIn'
import './public-shell.css'

/** The public pages a congregation publishes. */
export type PublicPage = 'zmanim' | 'donate' | 'contact'

/**
 * How the congregation says it can be reached, as the org row holds it.
 *
 * The org's own details and never a member's. Every field is nullable because a
 * congregation fills in what it has, so the phone's menu prints what is set and
 * says nothing where nothing is.
 */
export type PublicContact = {
  addressLine: string | null
  city: string | null
  region: string | null
  postalCode: string | null
  /** ISO 3166-1 alpha-2; named on the page only where it is not the US. */
  country?: string
  phone: string | null
  email: string | null
  /** The rav's line, resolved as the org reads resolve it. Every public read
   *  that draws this shell carries it; null where the shul has none. */
  rabbiLineHeResolved?: string | null
}

/** The little a picker needs to know about a congregation to offer it. */
export type ShulChoice = {
  shortId: string
  name: string
  nameHe: string | null
}

/** The directory destination an org switcher may offer for its product. */
export function directorySwitcherItem(product: Product | null) {
  const directory = product ? PRODUCT_DIRECTORIES[product] : null
  return directory
    ? { label: directory.allLabel, href: `/${directory.path}` }
    : null
}

/**
 * The shell every public page wears: the org's masthead, the site nav, and the
 * footer, with the page itself in between.
 *
 * It belongs to the SITE, not to any one page — which is the whole reason it is
 * a component rather than markup inside the zmanim screen. A visitor who lands
 * on the schedule and one who lands on the donation form are on the same site
 * and must be able to move between them; a header defined inside one page can
 * only ever serve that page, and the second copy is the one that goes stale.
 *
 * The masthead is the SITE's voice: the congregation's name in the serif, in
 * the script the shul chose for it — with its Hebrew name beside the English
 * where it chose both. It is not the letterhead — that belongs
 * to the printed sheet, which carries its own at its head.
 *
 * Give is offered only when the org can actually take money. `acceptsDonations`
 * is the processor's answer, not a setting — so the link is absent rather than
 * leading to a page whose only content is "this org isn't set up yet". The
 * caller passes it in from the org read it has already done; the shell asks
 * nobody, so opening a public page costs one request rather than two.
 *
 * On the platform's own address the name is also a PICKER — the same read the
 * caller already did, offered as a way across to another congregation. It is
 * the masthead rather than a separate control because the name is already the
 * answer to "whose site is this"; a chooser elsewhere would ask the reader to
 * find a second place for the same question. On an org's own domain it is a
 * plain name again: a congregation's site does not advertise its neighbors.
 */
export function PublicShell({
  name,
  nameHe,
  orgId,
  acceptsDonations = false,
  siblings,
  contact,
  current,
  bleed = false,
  masthead = null,
  kiosk = false,
  language = DEFAULT_PUBLIC_LANGUAGE,
  onLogin,
  children,
}: {
  name: string | null
  nameHe?: string | null
  /**
   * Which script the shul's site says each kind of text in — here, its name.
   * From the caller's org read, like `contact`.
   */
  language?: PublicLanguage
  /**
   * The org's public id — its mount point. Every link below is built from it
   * via `orgHref` rather than interpolated, so an org served on its own domain
   * (where the mount point is empty) gets correct links from the same code.
   * Null on a page that has not resolved an org, which links nowhere.
   */
  orgId: string | null
  /** Whether a donation would actually go through — from the caller's org read. */
  acceptsDonations?: boolean
  /**
   * Every congregation publishing here, including this one. Passed in from the
   * caller's existing list read rather than fetched, so the picker costs no
   * request; omitted (or a list of one) leaves the name as plain text.
   *
   * The donation page deliberately omits it. It is the one page someone arrives
   * at meaning to finish something, and a masthead offering to take them to a
   * different congregation is a way out of it — worth less than the request it
   * would cost.
   */
  siblings?: ShulChoice[]
  /**
   * How to reach the congregation, for the phone's menu and the rav's line
   * under the name. From the caller's own org read, like `acceptsDonations` —
   * the shell asks nobody, so a public page still costs one request.
   */
  contact?: PublicContact | null
  /**
   * Which nav item is this page. Omitted when the page is not one of them, so
   * nothing in the nav reads as current.
   */
  current?: PublicPage
  /**
   * The page lays out its own bands across the window, each keeping the
   * measure inside itself — a home page's rules and fills run edge to edge,
   * where a schedule or a form sits on the shell's measure.
   */
  bleed?: boolean
  /**
   * The masthead the org drew, which opens the page and already says whose
   * site this is. While it is on screen the page needs no bar; once it has
   * scrolled away the bar comes in, compact, carrying the name and the way
   * around the site.
   */
  masthead?: HTMLElement | null
  /** A shared-device giving flow has no links out to the public site. */
  kiosk?: boolean
  onLogin?: () => void
  children: ReactNode
}) {
  const pages = sitePages(orgId, acceptsDonations)

  // The name in the script the shul chose, the other standing in where the
  // chosen one is missing; the face follows the script.
  const said = say(language.name, name, nameHe)
  const inHebrew = !said.en && !!said.he
  const displayName = said.en ?? said.he ?? ''
  const besideName = said.en && said.he ? said.he : null
  const rav = contact?.rabbiLineHeResolved?.trim() || null

  // A picker needs somewhere else to go, and somewhere to go FROM: with one
  // congregation on the platform it would open onto the page you are reading.
  const canSwitch =
    isPlatformSurface() && !!orgId && !!siblings && siblings.length > 1
  const directoryItem = directorySwitcherItem(currentProduct())

  const chrome = useRef<HTMLDivElement>(null)
  useFollowInPlace(chrome)

  const sentinel = useRef<HTMLSpanElement>(null)
  const bar = useRef<HTMLElement>(null)
  const compact = useScrolledPast(sentinel, masthead, bar)

  const [menuOpen, setMenuOpen] = useState(false)
  // Sign in from the menu waits for the menu to go: the page behind it is
  // inert while it stands, and the sign-in screen focuses its first field as it
  // mounts — which an inert page refuses. So the menu closes first, and the
  // sign-in follows once the page is live again. Where the way in is a link to
  // the app instead, following it leaves the page and waits for nothing.
  const [signInNext, setSignInNext] = useState(false)
  useEffect(() => {
    if (menuOpen || !signInNext) return
    setSignInNext(false)
    onLogin?.()
  }, [menuOpen, signInNext, onLogin])
  // A menu with nothing in it is a control that opens onto nothing.
  const hasMenu = pages.length > 0 || !!onLogin

  if (kiosk)
    return (
      <div className="pubs" ref={chrome}>
        <div className="pubs__body" data-bleed={bleed ? '' : undefined}>
          {children}
        </div>
      </div>
    )

  return (
    <div
      className="pubs"
      ref={chrome}
      data-masthead={masthead ? '' : undefined}
    >
      {/*
        The masthead's room in the flow is held at its full height whatever the
        bar is showing, so compacting the bar moves nothing on the page: a bar
        that gave its height back as it shrank would shift the page under the
        reader, and the browser would scroll to compensate and undo the very
        scroll that compacted it.
      */}
      <div className="pubs__top">
        <span className="pubs__sentinel" ref={sentinel} aria-hidden />
      </div>

      <header
        ref={bar}
        className="pubs__bar"
        data-compact={compact ? '' : undefined}
        data-masthead={masthead ? '' : undefined}
      >
        <div className="pubs__measure pubs__bar-inner">
          {/* The rav's line sits under the shul's name, the way a shul's own
              stationery sets it. It belongs to the masthead at rest only: once
              the page scrolls the bar is a line of navigation, and it goes
              with the Hebrew name. Below the handheld edge the menu carries
              it. */}
          <div className="pubs__head">
            <div className="pubs__identity">
              {canSwitch ? (
                <span
                  className={`pubs__picker ${inHebrew ? 'pubs__picker--he' : ''}`}
                  lang={inHebrew ? 'he' : undefined}
                >
                  <Menu
                    ariaLabel="Choose a congregation"
                    label={displayName}
                    align="start"
                    items={[
                      ...siblings.map((s) => ({
                        label: s.name || s.nameHe || s.shortId,
                        // The other org's ROOT, not the page you happen to be on.
                        // Carrying the page across looks tidier and lands you on a
                        // donation form for a congregation that may not take
                        // donations; the root is the one address every org answers.
                        href: orgHref(s.shortId, ''),
                        selected: s.shortId === orgId,
                      })),
                      ...(directoryItem
                        ? [{ ...directoryItem, icon: LayoutGrid }]
                        : []),
                    ]}
                  />
                </span>
              ) : orgId ? (
                <a
                  className={`pubs__name ${inHebrew ? 'pubs__name--he' : ''}`}
                  href={orgHref(orgId, '')}
                  lang={inHebrew ? 'he' : undefined}
                  dir={inHebrew ? 'rtl' : undefined}
                >
                  {displayName}
                </a>
              ) : (
                <span
                  className={`pubs__name ${inHebrew ? 'pubs__name--he' : ''}`}
                >
                  {displayName}
                </span>
              )}
              {besideName ? (
                <span className="pubs__name-he" lang="he" dir="rtl">
                  {besideName}
                </span>
              ) : null}
            </div>
            {rav ? (
              <p className="pubs__rav" lang="he" dir="rtl">
                {rav}
              </p>
            ) : null}
          </div>

          {/* Site nav, not a tab strip: each item is its own page, so they are
              real links and the current one is stated rather than linked.

              The order is the reader's: the times first where the site carries
              them, because that is what a shul's site is opened for most
              often, then giving, then how to reach the org. Each is absent
              where the org has nothing for it, and the rest keep their order
              either way. */}
          <nav className="pubs__nav" aria-label="This site">
            {pages.map((p) => (
              <NavItem key={p.page} href={p.href} on={current === p.page}>
                {p.label}
              </NavItem>
            ))}
            <WayIn
              onSignIn={onLogin}
              variant="quiet"
              className="pubs__signin"
            />
          </nav>

          {/*
            The phone's one way in to everything beside the name. The pages and
            the sign-in compete with the name for 375px, so they collapse to one
            entry point; the name is not chrome and stays.
          */}
          <div className="pubs__compact">
            {hasMenu ? (
              <Button
                icon
                variant="secondary"
                aria-label="Open the menu"
                aria-haspopup="dialog"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen(true)}
              >
                <MenuIcon className="btn__icon" aria-hidden />
              </Button>
            ) : null}
          </div>
        </div>
      </header>

      {menuOpen ? (
        <SiteMenu
          name={displayName}
          nameHe={besideName}
          inHebrew={inHebrew}
          pages={pages}
          current={current}
          contact={contact}
          onLogin={
            onLogin
              ? () => {
                  setSignInNext(true)
                  setMenuOpen(false)
                }
              : undefined
          }
          onClose={() => setMenuOpen(false)}
        />
      ) : null}

      <div className="pubs__body" data-bleed={bleed ? '' : undefined}>
        {children}
      </div>

      {/* The site's pages, the reader's own setting, and the one place the
          site names the platform behind it.

          It belongs to the SITE, not to any one page, so it is the same on
          every one of them. It carries no contact details: the masthead names
          the shul, the Contact page and the home page set out how to reach
          it, and the phone's menu carries the number wherever a reader is.
          A line of them here would be a second statement on the pages that
          have one and a cramped one on the pages that do not. The pages lead:
          the way on from the foot of any page.

          It stands even where the page below it carries a letterhead foot of
          its own. The luach's belongs to the SHEET — it is what prints, and on
          paper there is no site to close — so the two are one artifact each
          rather than one fact twice, and a footer that came and went with the
          page would stop being the site's.

          The light and dark switch sits here, beside the platform line: it is
          the reader's own setting rather than somewhere the site goes, so it
          does not compete with the pages in the masthead. */}
      <footer className="pubs__foot">
        <div className="pubs__measure pubs__foot-inner">
          {pages.length > 0 ? (
            <nav className="pubs__foot-pages" aria-label="This site's pages">
              {/* The page you are on is stated, not linked, as the masthead
                  and the menu state it. */}
              {pages.map((p) =>
                current === p.page ? (
                  <span
                    key={p.page}
                    className="pubs__foot-page"
                    aria-current="page"
                  >
                    {p.label}
                  </span>
                ) : (
                  <a key={p.page} className="pubs__foot-page" href={p.href}>
                    {p.label}
                  </a>
                ),
              )}
            </nav>
          ) : null}
          <div className="pubs__foot-end">
            <ThemeToggle labeled />
            <PoweredBy
              name={productName(currentProduct() ?? undefined)}
              href="/"
            />
          </div>
        </div>
      </footer>
    </div>
  )
}

type SitePage = { page: PublicPage; label: string; href: string }

/**
 * The pages an org's site offers, in the order both navs read them.
 *
 * Declared once and drawn twice — the line of links on a wide bar and the
 * phone's menu are two layouts of one list, so neither can gain a page the
 * other does not have. "Give" names the donate page: it is what the visitor
 * does there, where "Donate" names the form. A page that has not resolved a
 * org has no site to offer, so it offers no pages.
 */
function sitePages(orgId: string | null, showGive: boolean): SitePage[] {
  if (orgId === null) return []
  return [
    ...(siteCarriesTimes()
      ? [
          {
            page: 'zmanim' as const,
            label: 'Zmanim',
            href: orgHref(orgId, 'zmanim'),
          },
        ]
      : []),
    ...(showGive
      ? [
          {
            page: 'donate' as const,
            label: 'Give',
            href: orgHref(orgId, 'donate'),
          },
        ]
      : []),
    { page: 'contact', label: 'Contact', href: orgHref(orgId, 'contact') },
  ]
}

/**
 * Whether the page has scrolled past its top, read off a sentinel at the head
 * of the masthead's room in the flow — or, under an org's own masthead, once
 * what is left of it would sit under the bar, which is the moment the bar has
 * nothing left to cover.
 *
 * Observed rather than measured on every scroll event: the answer changes twice
 * per visit, and an observer is told exactly then.
 */
function useScrolledPast(
  sentinel: RefObject<HTMLElement | null>,
  masthead: HTMLElement | null,
  bar: RefObject<HTMLElement | null>,
): boolean {
  const [past, setPast] = useState(false)
  useEffect(() => {
    const el = masthead ?? sentinel.current
    if (!el || typeof IntersectionObserver !== 'function') return
    const under = masthead ? (bar.current?.offsetHeight ?? 0) : 0
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry) setPast(!entry.isIntersecting)
      },
      { rootMargin: `-${under}px 0px 0px 0px` },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [sentinel, masthead, bar])
  return past
}

/**
 * The phone's menu: the whole site, covering the page.
 *
 * A page of its own rather than a dropdown, because at 375px it holds more than
 * a list: the pages, set large enough to hit with a thumb, then the two things a
 * visitor on a phone most often wants from a shul — to ring it and to
 * get there — then the way in for somebody who belongs to it. The pages lead
 * because they are what the entry point was reached for; the call follows
 * because it is the reason to be holding a phone.
 *
 * It covers the page, so it takes everything `useDismissable` gives a covering
 * surface: Escape, the page behind it inert, and focus handed back to the menu
 * button. A link inside it is followed in place like every other link on the
 * site, and the page changing underneath takes the menu with it.
 */
function SiteMenu({
  name,
  nameHe,
  inHebrew,
  pages,
  current,
  contact,
  onLogin,
  onClose,
}: {
  name: string
  nameHe: string | null
  inHebrew: boolean
  pages: SitePage[]
  current?: PublicPage
  contact?: PublicContact | null
  onLogin?: () => void
  onClose: () => void
}) {
  const panel = useRef<HTMLDivElement>(null)
  useDismissable(panel, onClose)
  useFollowInPlace(panel)

  // A link followed in place changes the address without remounting the page
  // that drew this menu, and it says so with a `popstate` as Back does — so the
  // address changing, either way, is what closes it.
  const latest = useRef(onClose)
  useEffect(() => {
    latest.current = onClose
  })
  useEffect(() => {
    const close = () => latest.current()
    window.addEventListener('popstate', close)
    return () => window.removeEventListener('popstate', close)
  }, [])

  // A covering menu belongs to the phone's masthead: once the window is wide
  // enough for the bar to show its pages, the menu button is gone and the menu
  // goes with it rather than standing over a layout that no longer needs it.
  // Read off the button's own box, so the edge stays the stylesheet's to say.
  useEffect(() => {
    const opener = document.querySelector('.pubs__compact button')
    const check = () => {
      if (opener && opener.getClientRects().length === 0) latest.current()
    }
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // The page behind stays where the reader left it: a drag inside a menu
  // shorter than the window would otherwise scroll the document underneath.
  useEffect(() => {
    const root = document.documentElement
    const was = root.style.overflow
    root.style.overflow = 'hidden'
    return () => {
      root.style.overflow = was
    }
  }, [])

  const postal = contact ? placeLine(contact) : ''
  const phone = contact?.phone?.trim() ?? ''
  const rav = contact?.rabbiLineHeResolved?.trim() || null

  return createPortal(
    <div
      ref={panel}
      className="pubs-menu"
      role="dialog"
      aria-modal="true"
      aria-label="Site menu"
      tabIndex={-1}
    >
      <div className="pubs-menu__head">
        <div className="pubs__identity">
          <span
            className={`pubs__name ${inHebrew ? 'pubs__name--he' : ''}`}
            lang={inHebrew ? 'he' : undefined}
            dir={inHebrew ? 'rtl' : undefined}
          >
            {name}
          </span>
          {nameHe ? (
            <span className="pubs__name-he" lang="he" dir="rtl">
              {nameHe}
            </span>
          ) : null}
        </div>
        <Button
          icon
          variant="secondary"
          aria-label="Close the menu"
          onClick={onClose}
        >
          <X className="btn__icon" aria-hidden />
        </Button>
      </div>

      {pages.length > 0 ? (
        <nav className="pubs-menu__pages" aria-label="This site">
          {pages.map((p) =>
            current === p.page ? (
              <span
                key={p.page}
                className="pubs-menu__page"
                aria-current="page"
              >
                {p.label}
              </span>
            ) : (
              <a key={p.page} className="pubs-menu__page" href={p.href}>
                {p.label}
              </a>
            ),
          )}
        </nav>
      ) : null}

      <div className="pubs-menu__foot">
        {/* The rav's line, which a phone's bar has no room for under two
            names: here it heads who the shul is and how to reach it. */}
        {rav ? (
          <p className="pubs-menu__rav" lang="he" dir="rtl">
            {rav}
          </p>
        ) : null}
        {phone ? (
          // The shul speaks for itself: many have no office, and the number
          // is as often the gabbai's as anybody's.
          <a className="pubs-menu__call" href={`tel:${telHref(phone)}`}>
            <span>Call us</span>
            <span className="pubs-menu__number">{phone}</span>
          </a>
        ) : null}
        {postal ? (
          <p className="pubs-menu__where">
            <span>{postal}</span>
            <a
              className="pubs-menu__link"
              href={directionsHref(postal)}
              target="_blank"
              rel="noreferrer"
            >
              Directions
            </a>
          </p>
        ) : null}
        <div className="pubs-menu__end">
          <WayIn
            onSignIn={onLogin}
            variant="quiet"
            className="pubs__signin pubs-menu__signin"
          />
          <PoweredBy
            name={productName(currentProduct() ?? undefined)}
            href="/"
          />
        </div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * A number as a phone will dial it. Punctuation is for reading, so the link
 * carries digits only — and a leading `+` survives, since it is what says the
 * rest is a country code rather than an area code.
 */
export function telHref(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  return phone.trim().startsWith('+') ? `+${digits}` : digits
}

/**
 * Directions to an address, as a maps address every phone opens.
 *
 * The universal form of the address rather than a platform's own scheme, so it
 * answers in whichever maps app the reader has, and in a browser where there is
 * none.
 */
export function directionsHref(postal: string): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(postal)}`
}

/** The page you are on is stated, not linked — there is nowhere to go. */
function NavItem({
  href,
  on,
  children,
}: {
  href: string
  on: boolean
  children: ReactNode
}) {
  return on ? (
    <span className="pubs__link" aria-current="page">
      {children}
    </span>
  ) : (
    <a className="pubs__link" href={href}>
      {children}
    </a>
  )
}
